import { toast } from '@/components/ui/use-toast';
import { downloadFilefromBuffer } from '@/lib/utils';
import type { BillingGetAllQuerySchemaType, BillingItemBaseType } from '@/types/billing.types';
import type { InvoiceResponse, InvoiceUpdateSchemaType } from '@/types/invoice.types';
import type { ListPageResponse } from '@/types/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';
import type { GenericErrorResponse, GenericErrorResponseError } from '@/types/api.types';

export const useGetInvoiceId = (invoiceNumber: string | undefined, refreshKey?: number) => {
  return useQuery<InvoiceResponse, GenericErrorResponseError>({
    queryKey: [invoiceNumber, refreshKey],
    queryFn: async () => {
      if (!invoiceNumber) throw new Error('Invoice Number is required');

      const response = await axios.get<InvoiceResponse>(`/invoice/${invoiceNumber}`, {});

      return response.data;
    },
    enabled: !!invoiceNumber,
  });
};

export const useSendInvoiceMail = () => {
  return useMutation<{ message: string }, AxiosError<GenericErrorResponse>, { invoiceNumber: string }>({
    mutationFn: async ({ invoiceNumber }) => {
      const response = await axios.post<{ message: string }>(`/invoice/${invoiceNumber}/email`);
      return response.data;
    },
  });
};

export const getBillingItems = async (query?: BillingGetAllQuerySchemaType) => {
  const response = await axios.get<ListPageResponse<'billingItems', BillingItemBaseType>>('/billing', {
    params: query,
  });
  return response;
};

export const downloadInvoicePDF = async (invoiceNumber: string | null | undefined) => {
  toast({
    description: 'Download will start shortly',
    duration: 3000,
  });
  try {
    if (!invoiceNumber) throw new Error('Invoice number is required');
    const response = await axios.get<Blob>(`/invoice/${invoiceNumber}/pdf`, {
      responseType: 'blob',
    });
    downloadFilefromBuffer(response.data, `${invoiceNumber}.pdf`, 'PDF');
  } catch (err) {
    toast({
      description: 'Unable to generate PDF, please try again',
      duration: 3000,
      variant: 'destructive',
    });
  }
};

export const useUpdateInvoiceMutation = () => {
  return useMutation<
    { message: string },
    GenericErrorResponse,
    { invoiceNumber: string; data: InvoiceUpdateSchemaType }
  >({
    mutationFn: async ({ invoiceNumber, data }) => {
      const response = await axios.patch<{ message: string }>(`/invoice/${invoiceNumber}`, {
        ...data,
      });
      return response.data;
    },
  });
};

export const bulkExportInvoices = async (invoiceNumbers: string[]) => {
  toast({
    description: 'Preparing invoices for bulk export',
    duration: 3000,
  });
  try {
    const response = await axios.post<Blob>(
      '/invoice/bulk-export',
      { invoiceNumbers },
      {
        responseType: 'blob',
      }
    );
    downloadFilefromBuffer(response.data, 'invoices_export.zip', 'ZIP');
  } catch (err) {
    toast({
      description: 'Unable to export invoices, please try again',
      duration: 3000,
      variant: 'destructive',
    });
  }
};

export const mergeInvoices = async (invoiceNumbers: string[], clientName?: string) => {
  toast({
    description: 'Merging selected invoices',
    duration: 3000,
  });
  try {
    const response = await axios.post<Blob>(
      '/invoice/merge',
      { invoiceNumbers, clientName },
      {
        responseType: 'blob',
      }
    );
    const fileName = clientName ? `${clientName.replace(/[^a-zA-Z0-9_-]/g, '_')}_invoices.pdf` : 'merged_invoices.pdf';
    downloadFilefromBuffer(response.data, fileName, 'PDF');
  } catch (err) {
    toast({
      description: 'Unable to merge invoices, please try again',
      duration: 3000,
      variant: 'destructive',
    });
  }
};
